import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const SectionDivider = () => (
  <div className="w-full h-px bg-[#C9A84C]/20" />
);

export default function PrivacyPolicy() {
  useEffect(() => {
    document.title = 'Privacy Policy | MANDIE OZEE.';
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFAF8]">
      <Navbar />

      <main>
        {/* Page Header */}
        <section className="pt-28 pb-10 md:pt-40 md:pb-16">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-3xl mx-auto">
              <p className="text-[#C9A84C] text-xs font-body tracking-[0.25em] uppercase mb-4">
                Legal
              </p>
              <h1 className="font-heading text-xl md:text-3xl lg:text-5xl text-[#1A1A1A] leading-tight mb-3">
                Privacy Policy
              </h1>
              <p className="font-body text-sm text-[#999999]">
                Last updated: May 15, 2026
              </p>
            </div>
          </div>
        </section>

        <SectionDivider />

        {/* Policy Content */}
        <section className="py-12 md:py-16">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-3xl mx-auto font-body text-sm text-[#555555] leading-relaxed space-y-10">

              {/* Intro */}
              <div>
                <p className="mb-4">
                  Mandie Ozee ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website mandieozee.com (the "Site") or purchase products from our online shop. Please read this policy carefully. If you do not agree with the terms of this policy, please do not access the Site.
                </p>
              </div>

              <SectionDivider />

              {/* Information We Collect */}
              <div>
                <h2 className="font-heading text-xl md:text-2xl text-[#1A1A1A] mb-4">
                  1. Information We Collect
                </h2>

                <h3 className="font-heading text-base text-[#1A1A1A] mb-2">
                  Personal Information
                </h3>
                <p className="mb-4">
                  When you place an order, sign up for our newsletter, submit a contact form, or join a waitlist, we may collect the following personal information:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-4 text-[#666666]">
                  <li>Full name</li>
                  <li>Email address</li>
                  <li>Shipping and billing address</li>
                  <li>Phone number (if provided)</li>
                  <li>Payment information (processed securely through third-party providers, we do not store full credit card details)</li>
                </ul>

                <h3 className="font-heading text-base text-[#1A1A1A] mb-2">
                  Automatically Collected Information
                </h3>
                <p className="mb-4">
                  When you access the Site, our servers may automatically log standard data provided by your web browser, including:
                </p>
                <ul className="list-disc list-inside space-y-1 mb-4 text-[#666666]">
                  <li>IP address</li>
                  <li>Browser type and version</li>
                  <li>Pages visited and time spent</li>
                  <li>Referring site or source</li>
                  <li>Device type and operating system</li>
                </ul>
              </div>

              <SectionDivider />

              {/* How We Use Information */}
              <div>
                <h2 className="font-heading text-xl md:text-2xl text-[#1A1A1A] mb-4">
                  2. How We Use Your Information
                </h2>
                <p className="mb-4">
                  We use the information we collect for the following purposes:
                </p>
                <ul className="list-disc list-inside space-y-1 text-[#666666]">
                  <li>To process and fulfill your orders, including shipping and delivery</li>
                  <li>To communicate with you about your order status, shipping updates, and customer support</li>
                  <li>To send newsletters, updates, and promotional content (only if you have opted in)</li>
                  <li>To improve our Site, products, and customer experience</li>
                  <li>To comply with legal obligations and protect against fraudulent transactions</li>
                  <li>To respond to your inquiries submitted through our contact form</li>
                </ul>
              </div>

              <SectionDivider />

              {/* Sharing Information */}
              <div>
                <h2 className="font-heading text-xl md:text-2xl text-[#1A1A1A] mb-4">
                  3. How We Share Your Information
                </h2>
                <p className="mb-4">
                  We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
                </p>
                <ul className="list-disc list-inside space-y-1 text-[#666666]">
                  <li><strong className="text-[#555555]">Service Providers:</strong> We may share information with trusted third-party vendors who assist us in operating the Site, processing payments, fulfilling orders, and delivering newsletters. These providers are contractually obligated to keep your information confidential.</li>
                  <li><strong className="text-[#555555]">Legal Compliance:</strong> We may disclose information if required by law, court order, or government regulation.</li>
                  <li><strong className="text-[#555555]">Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.</li>
                </ul>
              </div>

              <SectionDivider />

              {/* Cookies */}
              <div>
                <h2 className="font-heading text-xl md:text-2xl text-[#1A1A1A] mb-4">
                  4. Cookies and Tracking
                </h2>
                <p className="mb-4">
                  Our Site uses cookies and similar tracking technologies to enhance your browsing experience. Cookies are small data files stored on your device that help us remember preferences, analyze traffic, and understand how visitors interact with the Site.
                </p>
                <p className="mb-4">
                  You can control cookie preferences through your browser settings. Disabling cookies may affect certain features of the Site, including shopping cart functionality.
                </p>
                <p>
                  We may use analytics services (such as Google Analytics) that collect anonymized data about Site traffic and usage patterns. These services operate independently and are governed by their own privacy policies.
                </p>
              </div>

              <SectionDivider />

              {/* Data Security */}
              <div>
                <h2 className="font-heading text-xl md:text-2xl text-[#1A1A1A] mb-4">
                  5. Data Security
                </h2>
                <p className="mb-4">
                  We implement industry-standard security measures to protect your personal information, including SSL encryption for data transmission and secure server infrastructure. However, no method of electronic storage or transmission is 100% secure, and we cannot guarantee absolute security.
                </p>
                <p>
                  Payment transactions are processed through PCI-compliant third-party payment gateways. We do not store full credit card numbers or CVV codes on our servers.
                </p>
              </div>

              <SectionDivider />

              {/* Data Retention */}
              <div>
                <h2 className="font-heading text-xl md:text-2xl text-[#1A1A1A] mb-4">
                  6. Data Retention
                </h2>
                <p>
                  We retain your personal information for as long as necessary to fulfill the purposes outlined in this policy, unless a longer retention period is required by law. Order records are retained for accounting and legal compliance purposes. Newsletter subscription data is retained until you unsubscribe.
                </p>
              </div>

              <SectionDivider />

              {/* Your Rights */}
              <div>
                <h2 className="font-heading text-xl md:text-2xl text-[#1A1A1A] mb-4">
                  7. Your Rights
                </h2>
                <p className="mb-4">
                  Depending on your location, you may have the following rights regarding your personal data:
                </p>
                <ul className="list-disc list-inside space-y-1 text-[#666666]">
                  <li><strong className="text-[#555555]">Access:</strong> Request a copy of the personal data we hold about you.</li>
                  <li><strong className="text-[#555555]">Correction:</strong> Request correction of inaccurate or incomplete data.</li>
                  <li><strong className="text-[#555555]">Deletion:</strong> Request deletion of your personal data, subject to legal obligations.</li>
                  <li><strong className="text-[#555555]">Portability:</strong> Request transfer of your data to another service provider.</li>
                  <li><strong className="text-[#555555]">Opt-Out:</strong> Unsubscribe from marketing communications at any time using the link in any marketing email, or by contacting us directly.</li>
                </ul>
              </div>

              <SectionDivider />

              {/* Children */}
              <div>
                <h2 className="font-heading text-xl md:text-2xl text-[#1A1A1A] mb-4">
                  8. Children's Privacy
                </h2>
                <p>
                  Our Site and products are not directed at individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that a child under 18 has provided us with personal data, we will take steps to delete such information promptly.
                </p>
              </div>

              <SectionDivider />

              {/* Changes */}
              <div>
                <h2 className="font-heading text-xl md:text-2xl text-[#1A1A1A] mb-4">
                  9. Changes to This Policy
                </h2>
                <p>
                  We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated "Last updated" date. We encourage you to review this policy periodically. Continued use of the Site after changes constitutes acceptance of the updated policy.
                </p>
              </div>

              <SectionDivider />

              {/* Contact */}
              <div>
                <h2 className="font-heading text-xl md:text-2xl text-[#1A1A1A] mb-4">
                  10. Contact Us
                </h2>
                <p className="mb-4">
                  If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
                </p>
                <div className="space-y-1 text-[#666666]">
                  <p>Email: hello@mandieozee.com</p>
                  <p>Contact form: mandieozee.com/contact</p>
                </div>
              </div>

            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}